import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
} from "react";

import { useAuth } from "./AuthContext";
import { getAccessPermissions } from "@/services/subscriptionService";

// sonner toast
import { toast } from "@/components/ui/sonner";

const RoleAccessContext = createContext(null);

/* -------------------------------------------------- */
/* Hook */
/* -------------------------------------------------- */
export const usePermission = () => {
    const context = useContext(RoleAccessContext);
    if (!context) {
        throw new Error("usePermission must be used within RoleAccessProvider");
    }
    return context;
};

/**
 * Replaces the permissions seen by everything inside it — used by "My team"
 * pages, where what a manager may do comes from the company's manager duties
 * rather than their access role. `value` is { permissions, can, loading }.
 */
export const PermissionOverride = ({ value, children }) => (
    <RoleAccessContext.Provider value={value}>{children}</RoleAccessContext.Provider>
);

/* -------------------------------------------------- */
/* Provider */
/* -------------------------------------------------- */
export const RoleAccessProvider = ({ children }) => {
    const { user } = useAuth();

    const [permissions, setPermissions] = useState({});
    // Tracks which user object the current permissions belong to. Used to derive
    // `loading` during render (see below).
    const [loadedForUser, setLoadedForUser] = useState(null);

    /* -------------------------------------------------- */
    /* Fetch permissions */
    /* -------------------------------------------------- */
    useEffect(() => {
        let cancelled = false;

        if (!user) {
            setPermissions({});
            setLoadedForUser(null);
            return;
        }

        const fetchAccessRole = async () => {
            try {
                const response = await getAccessPermissions();
                if (cancelled) return;
                setPermissions(response?.features || {});
            } catch (error) {
                if (cancelled) return;
                console.error("Error fetching access permissions:", error);
                setPermissions({});
            } finally {
                if (!cancelled) setLoadedForUser(user);
            }
        };

        fetchAccessRole();

        return () => {
            cancelled = true;
        };
    }, [user]);

    /* -------------------------------------------------- */
    /* Loading state (derived)                            */
    /* -------------------------------------------------- */
    // Permissions are still "loading" whenever we have an authenticated user but
    // haven't finished fetching their permissions yet. Deriving this during
    // render (rather than via a separate setState) avoids a transient gap right
    // after the user is restored on a page refresh — previously the route guard
    // briefly saw `loading = false` with empty permissions and bounced the user
    // to the dashboard regardless of the route they were on.
    const loading = Boolean(user) && loadedForUser !== user;

    /* -------------------------------------------------- */
    /* Permission checker (with optional notify)          */
    /* -------------------------------------------------- */
    const can = useMemo(() => {
        return (feature, action = "view", notify = false) => {
            // admins bypass RBAC
            // if (user?.role && user.role.includes("admin")) return true;

            const featurePerm = permissions?.[feature];
            if(permissions.hasOwnProperty(feature)) {
                console.log("Checking permission for feature:", feature);
            }
            // no feature entry at all
            if (!featurePerm) {
                if (notify) {
                    toast.error("Access restricted", {
                        description: "You do not have permission to access this feature.",
                    });
                }
                return false;
            }

            // base access missing
            if (!featurePerm.view) {
                if (notify) {
                    toast.error("Access restricted", {
                        description: "You do not have permission to view this section.",
                    });
                }
                return false;
            }

            // viewing is allowed at this stage
            if (action === "view") return true;

            // check action permission
            const allowed = Boolean(featurePerm[action]);

            if (!allowed && notify) {
                toast.error("Action blocked", {
                    description:
                        "You do not have permission to perform this action.",
                });
            }

            return allowed;
        };
    }, [permissions, user]);

    /* -------------------------------------------------- */
    /* Provider value */
    /* -------------------------------------------------- */
    const value = {
        permissions,
        can,
        loading,
    };

    return (
        <RoleAccessContext.Provider value={value}>
            {children}
        </RoleAccessContext.Provider>
    );
};
